import * as Sentry from '@sentry/react';
import { useRef, useState } from 'react';

import { Upload } from 'tus-js-client';

import { getApiUrl } from '@api/API';

export const useUploadMultipleFiles = () => {
  const uploadsRef = useRef<Map<string, Upload>>(new Map());

  const [uploadProgresses, setUploadProgresses] = useState<
    Map<string, { percentage: number; bytesUploaded: number; bytesTotal: number }>
  >(new Map());

  const startUpload = (file: File): Promise<{ mediaId: string }> => {
    return new Promise(async (resolve, reject) => {
      const token = await localStorage.getItem('token');
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

      const upload = new Upload(file, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        endpoint: `${getApiUrl()}/v1/user/uploads`,
        chunkSize: 5 * 1024 * 1024, // 5MB — safely under the 10M nginx client_max_body_size
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onError: function (error) {
          console.log('Failed because: ' + error);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
        onProgress: function (bytesUploaded, bytesTotal) {
          const percentage = (bytesUploaded / bytesTotal) * 100;
          setUploadProgresses((prev) => {
            const next = new Map(prev);
            next.set(fileKey, { percentage, bytesUploaded, bytesTotal });
            return next;
          });
        },
        onSuccess: function () {},
        onAfterResponse: (req, res) => {
          try {
            const responseBody = res.getBody();
            if (responseBody) {
              resolve(JSON.parse(responseBody));
            }
          } catch (error) {
            Sentry.captureException(error);
            reject(error);
          }
        },
      });

      uploadsRef.current.set(fileKey, upload);

      upload.findPreviousUploads().then(function (previousUploads) {
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      });
    });
  };

  const startUploads = (files: File[]): Promise<{ mediaId: string }[]> => {
    return Promise.all(files.map(startUpload));
  };

  return {
    uploadProgresses,
    startUpload,
    startUploads,
  };
};

export const useUploadFile = () => {
  const fileRef = useRef<File>(undefined);
  const uploadRef = useRef<Upload>(undefined);

  const DEFAULT_PROGRESS = { percentage: 0, bytesUploaded: 0, bytesTotal: 0 };

  const [uploadProgress, setUploadProgress] = useState<{
    percentage: number;
    bytesUploaded: number;
    bytesTotal: number;
  }>(DEFAULT_PROGRESS);

  const startUpload = (file: File): Promise<{ mediaId: string }> => {
    return new Promise(async (resolve, reject) => {
      fileRef.current = file;

      const token = await localStorage.getItem('token');

      uploadRef.current = new Upload(file, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        endpoint: `${getApiUrl()}/v1/user/uploads`,
        chunkSize: 5 * 1024 * 1024, // 5MB — safely under the 10M nginx client_max_body_size
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onError: function (error) {
          console.log('Failed because: ' + error);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
        onProgress: function (bytesUploaded, bytesTotal) {
          const percentage = (bytesUploaded / bytesTotal) * 100;
          console.log(bytesUploaded, bytesTotal, percentage + '%');
          setUploadProgress({ percentage, bytesUploaded, bytesTotal });
        },
        onSuccess: function () {},
        onAfterResponse: (req, res) => {
          try {
            const responseBody = res.getBody();
            console.log(responseBody);

            if (responseBody) {
              resolve(JSON.parse(responseBody));
            }
          } catch (error) {
            console.log(error);
            Sentry.captureException(error);
            reject(error);
          }
        },
      });

      // Check if there are any previous uploads to continue.
      uploadRef.current.findPreviousUploads().then(function (previousUploads) {
        // Found previous uploads so we select the first one.
        if (previousUploads.length) {
          uploadRef.current?.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        uploadRef.current?.start();
      });
    });
  };

  return {
    uploadProgress,
    startUpload,
  };
};
